import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight } from 'lucide-react';
import CompanionPanelNotice, { STAGE_TONE } from '@/components/CompanionPanelNotice';
import { ResultBody } from './AssessmentResultPage';

const MyAssessmentsPage = () => {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/assessments/my/results`)
      .then((res) => {
        setData(res.data);
        if (res.data.results?.length) setOpenId(res.data.results[0].attempt_id);
      })
      .catch(() => setData({ results: [] }));
  }, []);

  if (!data) {
    return <div className="text-center py-20 text-gray-300">Loading your results…</div>;
  }

  const results = data.results || [];
  const open = results.find((r) => r.attempt_id === openId);

  return (
    <div className="max-w-4xl mx-auto pb-16" data-testid="my-assessments-page">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">My Results</h1>
        <p className="text-base md:text-lg text-gray-300">
          Your developmental profile across the program — profiling, not testing.
        </p>
      </div>

      {results.length === 0 ? (
        <Card data-testid="my-assessments-empty">
          <CardContent className="p-10 text-center space-y-4">
            <p className="text-gray-600">
              You have not completed an assessment yet. Your first one opens when you enter Module 2.
            </p>
            <Link to="/dashboard/modules">
              <Button className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white" data-testid="my-assessments-go-modules">
                Go to the Coaching Program <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <CompanionPanelNotice />

          <Card data-testid="my-assessments-summary">
            <CardContent className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Current stage</div>
                <div className="text-2xl font-bold text-gray-900" data-testid="summary-current-stage">
                  {data.current_stage}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Latest score</div>
                <div className="text-2xl font-bold text-gray-900" data-testid="summary-current-score">
                  {data.current_score}%
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Assessments completed</div>
                <div className="text-2xl font-bold text-gray-900" data-testid="summary-completed-count">
                  {data.completed_count}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3">
            {results.map((r) => {
              const tone = STAGE_TONE[r.overall_stage] || STAGE_TONE[1];
              const isOpen = r.attempt_id === openId;
              return (
                <Card key={r.attempt_id} className={isOpen ? `border-2 ${tone.border}` : ''}>
                  <CardContent className="p-5">
                    <button
                      className="w-full flex items-center justify-between gap-4 text-left"
                      onClick={() => setOpenId(isOpen ? null : r.attempt_id)}
                      data-testid={`result-row-${r.assessment_location_id}`}
                    >
                      <div>
                        <div className="font-semibold text-gray-900">{r.location_label}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(r.submitted_at).toLocaleDateString()}
                          {r.is_retake ? ` · retake #${r.attempt_number}` : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <Badge variant="outline" className={tone.text}>{r.overall_stage_name}</Badge>
                        <span className="text-xl font-bold text-gray-900">{r.overall_score}%</span>
                      </div>
                    </button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {open && (
            <div className="pt-2">
              <h2 className="text-base md:text-lg font-semibold text-white mb-4">
                {open.location_label} — full reading
              </h2>
              <ResultBody result={open} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyAssessmentsPage;
